import { isAddress, zeroAddress } from 'viem'

import { PANCAKE_SWAP_PERMIT_2_ADDRESS, PERMIT_2_ADDRESS } from '../../../consts/addresses'
import { Message } from '../../../interfaces/userRequest'
import {
  HumanizerTypedMessageModule,
  HumanizerVisualization,
  HumanizerWarning
} from '../interfaces'
import {
  getAction,
  getAddressVisualization,
  getDeadline,
  getLabel,
  getRecipientText,
  getToken,
  getWarning
} from '../utils'

// interfaces
// export interface PermitSingle {
//     details: PermitDetails
//     spender: string
//     sigDeadline: BigNumberish
//   }

// interface PermitBatch {
//   details: PermitDetails[]
//   spender: string
//   sigDeadline: BigNumberish
// }

// example
// const permitSingle: PermitSingle = {
//   details: {
//     token: tokenAddress,
//     amount: MaxAllowanceTransferAmount,
//     // You may set your own deadline - we use 30 days.
//     expiration: toDeadline(/* 30 days= */ 1000 * 60 * 60 * 24 * 30),
//     nonce
//   },
//   spender: spenderAddress,
//   // You may set your own deadline - we use 30 minutes.
//   sigDeadline: toDeadline(/* 30 minutes= */ 1000 * 60 * 60 * 30)
// }

interface PermitDetails {
  token?: string
  amount?: bigint
  expiration?: bigint
  nonce?: bigint
}

interface TokenPermissions {
  token?: string
  amount?: bigint
}

function isCompletePermission(
  permission: TokenPermissions
): permission is { token: string; amount: bigint } {
  return permission.token !== undefined && permission.amount !== undefined
}

// The witness struct is defined by the app that asks for the signature, so the
// address that receives the tokens can be under any of these common names
const WITNESS_RECIPIENT_FIELD_NAMES = ['recipient', 'receiver', 'to']

type TypedFields = { name: string; type: string }[] | undefined

const hasField = (fields: TypedFields, name: string, types: string[]) =>
  !!fields?.some((field) => field.name === name && types.includes(field.type))

// ZKX: returns null when the witness does not name exactly one recipient (none of the known
// field names, or several of them with different addresses). The summary would then hide
// where the tokens go, so the caller falls back to the raw message and its
// "unable to translate" warning instead of showing a reassuring "Approve" line.
// Only fields the witness struct declares as `address` count: EIP-712 hashes declared fields
// only, so an undeclared `recipient` in the message JSON is not part of what gets signed.
function getWitnessRecipient(
  witness: Record<string, any> | undefined,
  witnessFields: TypedFields
): string | null {
  if (!witness || typeof witness !== 'object') return null

  const recipients = WITNESS_RECIPIENT_FIELD_NAMES.filter((fieldName) =>
    hasField(witnessFields, fieldName, ['address'])
  )
    .map((fieldName) => witness[fieldName])
    .filter((value): value is string => typeof value === 'string' && isAddress(value))
  const distinct = new Set(recipients.map((recipient) => recipient.toLowerCase()))

  return distinct.size === 1 ? recipients[0]! : null
}

const WITNESS_PRIMARY_TYPES: Record<string, string> = {
  PermitWitnessTransferFrom: 'TokenPermissions',
  PermitBatchWitnessTransferFrom: 'TokenPermissions[]'
}

export const permit2Module: HumanizerTypedMessageModule = (message: Message) => {
  if (message.content.kind !== 'typedMessage') return { fullVisualization: [] }
  const tm = message.content
  if (
    !tm?.domain?.verifyingContract ||
    ![PERMIT_2_ADDRESS.toLowerCase(), PANCAKE_SWAP_PERMIT_2_ADDRESS.toLocaleLowerCase()].includes(
      tm.domain.verifyingContract.toLowerCase()
    )
  )
    return { fullVisualization: [] }

  // ZKX: what is shown must be what is signed, so the witness branch is picked by the
  // primaryType (the struct EIP-712 hashes) and every field it reads must be declared there
  const witnessPermittedType = WITNESS_PRIMARY_TYPES[tm.primaryType as string]
  const primaryFields: TypedFields = witnessPermittedType ? tm.types?.[tm.primaryType] : undefined
  const witnessTypeName = primaryFields?.find((field) => field.name === 'witness')?.type

  if (
    witnessPermittedType &&
    witnessTypeName &&
    hasField(primaryFields, 'permitted', [witnessPermittedType]) &&
    hasField(primaryFields, 'spender', ['address']) &&
    hasField(primaryFields, 'nonce', ['uint256']) &&
    hasField(primaryFields, 'deadline', ['uint256']) &&
    hasField(tm.types?.TokenPermissions, 'token', ['address']) &&
    hasField(tm.types?.TokenPermissions, 'amount', ['uint256'])
  ) {
    const { permitted, spender, nonce, deadline, witness } = tm.message
    if ([permitted, spender, nonce, deadline].some((field) => field === undefined))
      return { fullVisualization: [] }

    const permissions: TokenPermissions[] = Array.isArray(permitted) ? permitted : [permitted]
    const completePermissions = permissions.filter(isCompletePermission)
    if (!completePermissions.length || completePermissions.length !== permissions.length)
      return { fullVisualization: [] }

    const tokenVisualizations = completePermissions
      .map(({ token, amount }) => [getToken(token, amount), getLabel('and')])
      .flat()
      .slice(0, -1)

    const recipient = getWitnessRecipient(witness, tm.types?.[witnessTypeName])
    if (!recipient) return { fullVisualization: [] }
    // A zero recipient does not mean the tokens are burned. Permit2 only checks
    // the signature, so whoever executes the transfer picks where the tokens go
    const isRecipientChosenLater = recipient.toLowerCase() === zeroAddress
    const warnings: HumanizerWarning[] = isRecipientChosenLater
      ? [
          getWarning(
            'This request does not say who receives the tokens. Whoever submits it decides where the tokens go. Sign it only if you fully trust the app.',
            'PERMIT2_MISSING_RECIPIENT'
          )
        ]
      : []

    const recipientVisualizations = () => {
      // Plain text, not bold: a bold label is a separate slot in the translated sentence and
      // would stay English. The warning above already calls this out
      if (isRecipientChosenLater) return [getLabel('and send them to anyone')]

      return getRecipientText(message.accountAddr, recipient)
    }

    return {
      fullVisualization: [
        getAction('Approve'),
        getAddressVisualization(spender),
        getLabel('to transfer'),
        ...tokenVisualizations,
        ...recipientVisualizations(),
        getDeadline(deadline)
      ],
      warnings
    }
  }

  // A witness primaryType that failed the checks above is not read as another Permit2 struct
  if (witnessPermittedType) return { fullVisualization: [] }

  // ZKX: read the struct the signature is for (primaryType), not whichever one types declares
  const messageType = ['PermitSingle', 'PermitBatch', 'PermitTransferFrom'].includes(
    tm.primaryType as string
  )
    ? tm.types?.[tm.primaryType]?.[0]?.type
    : undefined

  if (!messageType) return { fullVisualization: [] }
  if (messageType === 'TokenPermissions') {
    const { spender, nonce, deadline, permitted } = tm.message
    if ([spender, nonce, deadline, permitted].some((a) => a === undefined))
      return { fullVisualization: [] }
    const { token, amount } = permitted
    if (token === undefined || amount === undefined) return { fullVisualization: [] }
    return {
      fullVisualization: [
        getAction('Approve'),
        getAddressVisualization(spender),
        getLabel('to use'),
        getToken(token, amount),
        getDeadline(deadline)
      ]
    }
  } else if (['PermitDetails', 'PermitDetails[]'].includes(messageType)) {
    if (!tm.message.details) return { fullVisualization: [] }
    const permits: { token: string; amount: bigint }[] = (
      messageType === 'PermitDetails' ? [tm.message.details] : tm.message.details
    ).map((d: PermitDetails) => ({
      token: d.token,
      amount: d.amount
    }))
    if (permits.some((p) => p.amount === undefined || p.token === undefined))
      return { fullVisualization: [] }

    if (!permits.length) return { fullVisualization: [] }

    const permitVisualizations = permits
      .map(({ token, amount }) => [
        getAddressVisualization(tm.message.spender),
        getLabel('to use'),
        getToken(token, amount),
        getLabel('and')
      ])
      .flat()
      .slice(0, -1)

    return {
      fullVisualization: [
        getAction('Approve'),
        ...permitVisualizations,
        getDeadline(tm.message.sigDeadline) as HumanizerVisualization
      ]
    }
  }

  return { fullVisualization: [] }
}
